import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, MapPin, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const CATEGORIES = ["airport", "city", "lagoon", "terminal", "hotel", "custom-area", "other"];

export default function AdminTransferLocations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | location
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/transfer-locations");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[idx], next[target]] = [next[target], next[idx]];
    setRows(next); // optimistic
    try {
      await api.post("/admin/transfer-locations/reorder", {
        ordered_ids: next.map((r) => r.id),
      });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const toggleActive = async (loc) => {
    try {
      await api.put(`/admin/transfer-locations/${loc.id}`, { active: !loc.active });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/transfer-locations/${loc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/admin/transfer-locations/${editing.id}`, form);
        toast.success("Location updated");
      } else {
        await api.post("/admin/transfer-locations", form);
        toast.success("Location created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-transfer-locations-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Transfer Locations</h1>
          <p className="text-sm text-slate-500">
            Manage predefined pickup &amp; drop-off locations shown on the transfer booking form.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="tl-new-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={14} /> New location
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3">Pickup</th>
                <th className="px-4 py-3">Drop-off</th>
                <th className="px-4 py-3">Exact addr.</th>
                <th className="px-4 py-3">Quote req.</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">No locations yet</td></tr>
              )}
              {!loading &&
                rows.map((r, idx) => (
                  <tr key={r.id} data-testid={`tl-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <span className="tabular-nums text-slate-500 w-6">{r.sort_order}</span>
                        <button
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0}
                          data-testid={`tl-up-${r.id}`}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                          aria-label="Move up"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          onClick={() => move(idx, 1)}
                          disabled={idx === rows.length - 1}
                          data-testid={`tl-down-${r.id}`}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                          aria-label="Move down"
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{r.name}</p>
                          {r.category && <p className="text-xs text-slate-500 capitalize">{r.category}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{r.code}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(r)}
                        data-testid={`tl-toggle-active-${r.id}`}
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${
                          r.active
                            ? "bg-emerald-100 text-emerald-900 ring-emerald-200"
                            : "bg-slate-100 text-slate-600 ring-slate-200"
                        }`}
                      >
                        {r.active ? "Active" : "Disabled"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.available_for_pickup ? <span className="text-emerald-700">Yes</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.available_for_dropoff ? <span className="text-emerald-700">Yes</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.requires_exact_address ? <span className="inline-flex items-center rounded-full bg-sky-100 text-sky-900 ring-1 ring-inset ring-sky-200 px-2 py-0.5 text-[10px] font-semibold">Required</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.quote_required ? (
                        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200 px-2 py-0.5 text-[10px] font-semibold">Quote</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`tl-edit-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`tl-delete-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <LocationEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function LocationEditor({ initial, onCancel, onSave, saving }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    name: initial.name || "",
    code: initial.code || "",
    category: initial.category || "",
    description: initial.description || "",
    sort_order: initial.sort_order ?? "",
    active: initial.active !== undefined ? initial.active : true,
    available_for_pickup: initial.available_for_pickup !== undefined ? initial.available_for_pickup : true,
    available_for_dropoff: initial.available_for_dropoff !== undefined ? initial.available_for_dropoff : true,
    requires_exact_address: !!initial.requires_exact_address,
    quote_required: !!initial.quote_required,
    fixed_price_eligible: initial.fixed_price_eligible !== undefined ? !!initial.fixed_price_eligible : true,
    notes: initial.notes || "",
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      category: form.category || null,
      description: form.description || null,
      sort_order: form.sort_order === "" ? undefined : Number(form.sort_order),
      active: !!form.active,
      available_for_pickup: !!form.available_for_pickup,
      available_for_dropoff: !!form.available_for_dropoff,
      requires_exact_address: !!form.requires_exact_address,
      quote_required: !!form.quote_required,
      fixed_price_eligible: !!form.fixed_price_eligible,
      notes: form.notes || null,
    };
    onSave(payload);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-6"
      onClick={onCancel}
      data-testid="tl-editor-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-4 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-semibold">{isEdit ? "Edit location" : "New location"}</h3>
          <button type="button" onClick={onCancel} aria-label="close" className="text-slate-500">
            <X size={18} />
          </button>
        </div>

        <Labeled label="Name *">
          <input
            data-testid="tl-field-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
          />
        </Labeled>

        <div className="grid gap-3 sm:grid-cols-2">
          <Labeled label="Code / slug">
            <input
              data-testid="tl-field-code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="auto-generated from name"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:border-slate-900"
            />
          </Labeled>
          <Labeled label="Category">
            <select
              data-testid="tl-field-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
            >
              <option value="">—</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Labeled>
        </div>

        <Labeled label="Description (internal)">
          <textarea
            data-testid="tl-field-description"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
          />
        </Labeled>

        <div className="grid gap-3 sm:grid-cols-2">
          <Labeled label="Sort order">
            <input
              type="number"
              data-testid="tl-field-sort"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              placeholder="auto"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
            />
          </Labeled>
          <div className="flex items-end">
            <Toggle
              label="Active"
              testid="tl-field-active"
              checked={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
            />
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <Toggle
            label="Show in Pickup dropdown"
            testid="tl-field-pickup"
            checked={form.available_for_pickup}
            onChange={(v) => setForm({ ...form, available_for_pickup: v })}
          />
          <Toggle
            label="Show in Drop-off dropdown"
            testid="tl-field-dropoff"
            checked={form.available_for_dropoff}
            onChange={(v) => setForm({ ...form, available_for_dropoff: v })}
          />
          <Toggle
            label="Requires exact address / hotel"
            testid="tl-field-requires-address"
            checked={form.requires_exact_address}
            onChange={(v) => setForm({ ...form, requires_exact_address: v })}
          />
        </div>

        <Toggle
          label="Fixed-price eligible (for future pricing)"
          testid="tl-field-fixed-price"
          checked={form.fixed_price_eligible}
          onChange={(v) => setForm({ ...form, fixed_price_eligible: v })}
        />
        <Toggle
          label="Quote required (force manual quote for any route using this location)"
          testid="tl-field-quote-required"
          checked={form.quote_required}
          onChange={(v) => setForm({ ...form, quote_required: v })}
        />

        <Labeled label="Internal notes">
          <textarea
            data-testid="tl-field-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
          />
        </Labeled>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="tl-editor-save"
            className="flex-1 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create location"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
      />
      {label}
    </label>
  );
}
